import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useControls } from 'leva';
import * as THREE from 'three';
import { MeshTransmissionMaterial } from '@react-three/drei';

export default function RandomTorus() {
    const { torusSize, tubeSize, rotationSpeed } = useControls('Random Torus Config', {
        torusSize: { value: 9, min: 5, max: 20, step: 0.1 },
        tubeSize: { value: 0.3, min: 0.1, max: 1, step: 0.01 },
        rotationSpeed: { value: 0.3, min: 0.1, max: 1, step: 0.01 },
    });

    const {
        color,
        transmission,
        roughness,
        thickness,
        ior,
        chromaticAberration,
        anisotropy,
        blur,
        opacity
    } = useControls('Torus Material Config', {
        thickness: { value: 3, min: 0, max: 3, step: 0.001 },
        roughness: { value: 0.05, min: 0, max: 1, step: 0.01 },
        transmission: {value: 1, min: 0, max: 1, step: 0.01},
        ior: { value: 1.2, min: 0, max: 3, step: 0.01 },
        chromaticAberration: { value: 0.05, min: 0, max: 1},
        opacity: { value: 1, min: 0, max: 1, step: 0.01 }
    });

    // Generate torus data once
    const toruses = useRef(
        [
            {
                position: [-0.75, 0, 0],
                size: torusSize * 1.05,
                tube: tubeSize * 1.05,
                rotationSpeed: rotationSpeed * 0.8,
                rotationFactor: 1.1 // normal
            },
            {
                position: [-0.75, 0.25, -0.25],
                size: torusSize * 1.05,
                tube: tubeSize * 0.8,
                rotationSpeed: rotationSpeed * 1.1,
                rotationFactor: 1.5 // rotate more
            }
        ]
    );
    const torusRefs = useRef([]);

    useFrame((state) => {
        const elapsedTime = state.clock.getElapsedTime();
        torusRefs.current.forEach((torus, index) => {
            if (torus) {
                const { rotationSpeed, rotationFactor } = toruses.current[index];
                // Offset the phase for each torus so their rotation is different
                const t = ((elapsedTime * rotationSpeed * 0.25 + index * 0.23) % 1);
                const rotation = (Math.PI / 16) * (1 - Math.cos(2 * Math.PI * t)) * rotationFactor;
                torus.rotation.set(
                    -Math.PI / 1.7 + rotation,
                    rotation,
                    rotation
                );
            }
        });
    });

    return (
        <>
            {toruses.current.map((torus, index) => (
                <mesh
                    key={index}
                    ref={el => (torusRefs.current[index] = el)}
                    position={torus.position}
                    receiveShadow={false}
                >
                    <torusGeometry args={[torus.size, torus.tube, 12, 64]} />
                    <MeshTransmissionMaterial
                        color={color}
                        transmission={transmission}
                        roughness={roughness}
                        thickness={thickness}
                        ior={ior}
                        chromaticAberration={chromaticAberration}
                        anisotropy={anisotropy}
                        blur={blur}
                        opacity={opacity}
                        transparent
                    />
                </mesh>
            ))}
        </>
    );
}
